import * as React from 'react';
import { useRecordContext } from 'react-admin';
import playerPlaceholder from '../assets/player-placeholder.svg';
import { getPlayerPhotoUrl } from './playerViewModel';
import { BASE_URL } from '../BASE_CONSTS';
import Cookies from '../provider/helpers/Cookies';

const CHARTS = [
  { key: 'Hit', title: 'Сила удара', unit: 'км/ч', decimals: 0 },
  { key: 'Speed_s_razbega', title: '10 метров с разбега', unit: 'км/ч', decimals: 1 },
  { key: 'FootSkill2', title: 'FootSkill test', unit: 'удары', decimals: 0 },
  { key: 'ExstraColorDriblingTest', title: 'Месси тест 1', unit: 'касания', decimals: 0, valid: (value) => value <= 5 },
  { key: 'Agility', title: 'Agility test', unit: 'секунды', decimals: 1 },
];

const formatDate = (value) => {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1].slice(2)}` : String(value);
};

const buildSeries = (statistics, chart) =>
  (Array.isArray(statistics) ? statistics : [])
    .map((row, index) => ({
      index,
      date: row?.date || '',
      value: Number(row?.[chart.key]),
    }))
    .filter((point) => Number.isFinite(point.value) && point.value > 0 && (!chart.valid || chart.valid(point.value)));

const LineChartCard = ({ chart, statistics }) => {
  const points = buildSeries(statistics, chart);
  const width = 588;
  const height = 312;
  const padding = { top: 24, right: 24, bottom: 54, left: 72 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  if (!points.length) {
    return (
      <article className="fs-chart-card">
        <header><div><h3>{chart.title}</h3><span>{chart.unit}</span></div></header>
        <div className="fs-chart-empty">Пока нет результатов для построения графика.</div>
      </article>
    );
  }

  const values = points.map((point) => point.value);
  let min = Math.min(...values);
  let max = Math.max(...values);
  const baseRange = max - min;
  const margin = baseRange > 0 ? baseRange * 0.16 : Math.max(Math.abs(max) * 0.12, 1);
  min = Math.max(0, min - margin);
  max += margin;

  const xFor = (index) => padding.left + (points.length === 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yFor = (value) => padding.top + innerHeight - ((value - min) / (max - min || 1)) * innerHeight;
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index)} ${yFor(point.value)}`).join(' ');
  const tickCount = 5;
  const yTicks = Array.from({ length: tickCount }, (_, index) => max - ((max - min) / (tickCount - 1)) * index);
  const labelStep = Math.max(1, Math.ceil(points.length / 5));

  return (
    <article className="fs-chart-card">
      <header>
        <div><h3>{chart.title}</h3><span>{chart.unit}</span></div>
        <strong>{points[points.length - 1].value.toFixed(chart.decimals)}</strong>
      </header>
      <div className="fs-chart-card__canvas">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`График ${chart.title}`}>
          <defs>
            <clipPath id={`clip-${chart.key}`}>
              <rect x={padding.left} y={padding.top} width={innerWidth} height={innerHeight} rx="4" />
            </clipPath>
          </defs>

          {yTicks.map((tick, index) => {
            const y = padding.top + (index / (tickCount - 1)) * innerHeight;
            return (
              <g key={tick}>
                <line className="fs-chart-grid-line" x1={padding.left} x2={width - padding.right} y1={y} y2={y} />
                <text className="fs-chart-axis-label" x={padding.left - 12} y={y + 4} textAnchor="end">
                  {tick.toFixed(chart.decimals)}
                </text>
              </g>
            );
          })}

          <g clipPath={`url(#clip-${chart.key})`}>
            {points.length > 1 ? <path className="fs-chart-line" d={path} /> : null}
            {points.map((point, index) => (
              <circle className="fs-chart-point" key={`${point.date}-${point.index}`} cx={xFor(index)} cy={yFor(point.value)} r="5">
                <title>{`${formatDate(point.date)} — ${point.value.toFixed(chart.decimals)} ${chart.unit}`}</title>
              </circle>
            ))}
          </g>

          {points.map((point, index) => {
            if (index % labelStep !== 0 && index !== points.length - 1) return null;
            return (
              <text className="fs-chart-date-label" key={`date-${point.index}`} x={xFor(index)} y={height - 18} textAnchor="middle">
                {formatDate(point.date)}
              </text>
            );
          })}
        </svg>
      </div>
    </article>
  );
};

const CustomChartAr = () => {
  const record = useRecordContext();
  const playerId = record && record.id;
  const [player, setPlayer] = React.useState(record || null);
  const [loading, setLoading] = React.useState(Boolean(playerId));
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!playerId) {
      setPlayer(record || null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const token = Cookies.getCookie('token');
    setLoading(true);
    setError('');

    fetch(`${String(BASE_URL).replace(/\/+$/, '')}/footballers/${playerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setPlayer(data);
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setPlayer(record || null);
          setError(`Не удалось загрузить статистику игрока: ${fetchError.message}`);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [playerId]);

  if (!record && !player) return <div className="fs-admin-empty">Игрок не найден.</div>;
  if (loading && !(player?.Statistics || []).length) return <div className="fs-admin-empty">Загрузка статистики игрока…</div>;

  const resolvedPlayer = player || record;
  const statistics = Array.isArray(resolvedPlayer?.Statistics) ? resolvedPlayer.Statistics : [];
  const photo = getPlayerPhotoUrl(resolvedPlayer, undefined, 'thumbnail') || playerPlaceholder;

  return (
    <div className="fs-player-charts">
      <header className="fs-player-charts__header">
        <img
          src={photo}
          alt="Фотография игрока"
          onError={(event) => { event.currentTarget.src = playerPlaceholder; }}
        />
        <div>
          <span>СТАТИСТИКА ИГРОКА</span>
          <h1>{[resolvedPlayer?.lastname, resolvedPlayer?.firstname].filter(Boolean).join(' ') || 'Без имени'}</h1>
          <p>{resolvedPlayer?.team || 'Команда не указана'} · {resolvedPlayer?.playerPosition || 'Позиция не указана'}</p>
        </div>
        <div className="fs-player-charts__summary">
          <strong>{statistics.length}</strong>
          <span>результатов</span>
        </div>
      </header>

      {error ? <div className="fs-admin-alert fs-admin-alert--error">{error}</div> : null}
      {!statistics.length ? <div className="fs-admin-empty">У игрока пока нет статистики для отображения.</div> : null}

      <div className="fs-chart-grid">
        {CHARTS.map((chart) => <LineChartCard key={chart.key} chart={chart} statistics={statistics} />)}
      </div>
    </div>
  );
};

export default CustomChartAr;
